import { Typography, CircularProgress, Stack, Icon, Box, Paper } from '@ace-clublabs/shared-component-library';
import { numberHelper } from '../../utils/numberHelper';
import { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { MembershipAppStateContext } from '../MembershipAppState/MembershipAppState';
import { CartProductCode, FlowType, MembershipLevel } from '@ace-clublabs/common-membership-api-types';
import { convertBillPlanToTerm } from '../../utils/helpers';
import { DiscountRule, Pages } from '@lib/types';
import { getRecurringPeriodLabel } from '../../utils/stringHelper';

interface ITotalCostHeader {
  formattedCost?: string;
  strikethrough?: boolean;
}

export const TotalCostHeader = ({ formattedCost, strikethrough }: ITotalCostHeader) => {
  const { membershipInfo, sessionData, isPriceCartLoading } = useContext(MembershipAppStateContext);
  const history = useHistory();
  const membershipLevel = sessionData.formData.membershipLevel;
  const ml =
    membershipLevel == undefined ||
    membershipLevel == MembershipLevel.CLASSIC_RV ||
    membershipLevel == MembershipLevel.CLASSIC
      ? MembershipLevel.CLASSIC
      : membershipLevel == MembershipLevel.PLUS_RV || membershipLevel == MembershipLevel.PLUS
        ? MembershipLevel.PLUS
        : membershipLevel == MembershipLevel.PREMIER_RV || membershipLevel == MembershipLevel.PREMIER
          ? MembershipLevel.PREMIER
          : membershipLevel;
  const recurringPeriodLabel = getRecurringPeriodLabel(sessionData);

  //check if there's free associate
  const term = convertBillPlanToTerm(sessionData.formData?.billPlan!);
  const hasFreeAssociate = membershipInfo?.membershipCost?.[ml]?.[term]?.membershipDiscounts?.find(
    (rule) => rule?.rule == DiscountRule.FREE_ASSOCIATE
  );
  const numberOfAssociates = sessionData.formData.associates?.length || 0;

  //Get primary member discount for T-Mobile display
  const primaryDiscountAmount = membershipInfo?.membershipCost?.[ml]?.[term]?.primary?.discountAmount || 0;

  //check if the admission fee is waived
  const admissionFeeCost = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.ADMISSION);
  const admissionFeeWaived = admissionFeeCost?.discountedPrice === 0;

  //check if employee discount is applied
  const employeeDiscountApplied = sessionData.flowType === FlowType.EMPLOYEE && primaryDiscountAmount;

  //check if the autopay discount is applied
  const autopayDiscount = sessionData?.priceCart?.items?.find((item) => item.code === CartProductCode.AUTOPAY_DISCOUNT);
  const autopayDiscountApplied = (autopayDiscount?.price || 0) > 0;

  //get the gift card value
  const giftCardValue = membershipInfo?.giftDetails?.giftCardValue;

  const checkedIcon = (
    <Icon sx={{ fontSize: '1.25rem', color: '#4F8C24' }} iconName="circle-check-solid" className="checked-icon" />
  );

  const loadingIcon = (
    <CircularProgress
      variant="indeterminate"
      size={20}
      className="total-cost-spinner"
      sx={{
        lineHeight: '1rem',
        color: '#6C7175',
        '& circle': { stroke: '#6C7175' },
      }}
    />
  );

  const BenefitBulletsList = () => {
    let benefitsList: { className: string; content: string; unredeemed?: boolean; onClick?: () => void }[] = [];
    if (sessionData.flowType === FlowType.T_MOBILE && primaryDiscountAmount) {
      benefitsList.push({
        className: 't-mobile-year-on-us-bullet',
        content: `Year on Us by T-Mobile (${numberHelper.formatMoney(primaryDiscountAmount)} value)`,
      });
    }
    if (employeeDiscountApplied) {
      benefitsList.push({
        className: 'employee-discount-bullet',
        content: `Employee discount (${numberHelper.formatMoney(primaryDiscountAmount)} value)`,
      });
    }
    if (sessionData.flowType === FlowType.GIFT && giftCardValue && giftCardValue > 0) {
      benefitsList.push({ className: 'gift-card-bullet', content: `$${giftCardValue} gift card` });
    }
    if (hasFreeAssociate && hasFreeAssociate.displayCount > 0) {
      benefitsList.push({
        className: 'free-member-bullet',
        content: `${hasFreeAssociate.displayCount} free or discounted family member${hasFreeAssociate.displayCount > 1 ? 's' : ''}`,
        unredeemed: numberOfAssociates < hasFreeAssociate.displayCount,
        onClick: () => history.push(Pages.AddAssociatePage),
      });
    }
    if (admissionFeeWaived) {
      benefitsList.push({ className: 'admission-waived-bullet', content: 'Waived admission fee' });
    }
    if (autopayDiscountApplied) {
      benefitsList.push({ className: 'autopay-discount-bullet', content: 'Auto Pay discount applied' });
    }

    return benefitsList;
  };

  const BenefitBulletsSection = () => {
    const benefits = BenefitBulletsList();
    return (
      benefits.length > 0 && (
        <Stack sx={{ gap: '5px', padding: '1rem' }} className="promotion-bullets-section">
          <Typography variant="b2Regular" className="promotion-title" sx={{ color: '#6C7175', marginBottom: '4px' }}>
            Your promotions:
          </Typography>
          {benefits.map((benefit, index) =>
            benefit.unredeemed ? (
              <Stack
                key={index}
                direction="row"
                className={`banner-${benefit.className}`}
                onClick={benefit.onClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    benefit.onClick?.();
                  }
                }}
                sx={{
                  backgroundColor: '#F3E9FE',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: '#E9D7FD',
                    cursor: 'pointer',
                  },
                }}
              >
                <Typography className={benefit.className} variant="b1Regular" sx={{ color: '#404040' }}>
                  {benefit.content}
                </Typography>
                <Icon
                  iconName="chevron-right-regular"
                  className={`arrow-icon-${benefit.className}`}
                  sx={{ fontSize: '1rem', color: '#3D4043' }}
                />
              </Stack>
            ) : (
              <Stack direction="row" justifyContent="space-between" alignItems="center" key={index}>
                <Stack direction="row" sx={{ gap: '4px' }} alignItems="center">
                  {checkedIcon}
                  <Typography
                    className={benefit.className}
                    variant="b1Regular"
                    sx={{ color: '#404040', lineHeight: '28px' }}
                  >
                    {benefit.content}
                  </Typography>
                </Stack>
              </Stack>
            )
          )}
        </Stack>
      )
    );
  };

  return (
    <Paper
      className="membership-card-container"
      sx={{
        width: '100%',
        borderRadius: '0.5rem',
        boxShadow: '0 -2px 1px 0 rgba(8, 17, 34, 0.04), 0 2px 4px 0 rgba(8, 17, 34, 0.20)',
      }}
    >
      <Box className="card-details">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ backgroundColor: '#F7F5F1', padding: '1rem' }}
        >
          <Typography className="card-title" variant="b1Medium" sx={{ fontWeight: '700' }}>
            Total:
          </Typography>
          <Stack direction="row" className="card-price" justifyContent="space-between">
            <Stack direction="row" gap="0.5rem">
              <Stack direction="row" alignItems="center" className="membership-card-total-container">
                <>
                  {isPriceCartLoading ? (
                    loadingIcon
                  ) : (
                    <Typography
                      className={strikethrough ? 'strikethrough membership-price-total' : 'membership-price-total'}
                      variant="h6"
                      style={{ fontWeight: 700 }}
                    >
                      {formattedCost ?? numberHelper.formatMoney(sessionData?.priceCart?.total ?? 0.0)}
                    </Typography>
                  )}
                  <Typography
                    variant="b2Regular"
                    className="membership-price-label"
                    sx={{ color: '#6C7175', whiteSpace: 'nowrap', display: 'inline', marginLeft: '0.25rem' }}
                  >
                    {recurringPeriodLabel}
                  </Typography>
                </>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </Box>
      <BenefitBulletsSection />
    </Paper>
  );
};
